/**
 * List Page Template
 * Page template for table/list views with filtering
 */

import React from 'react';
import { Input, Space, Button } from 'antd';
import { SearchOutlined, FilterOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import TrustGridPage from './TrustGridPage';
import Section from './Section';
import { spacing } from '../../theme/designTokens';

const { Search } = Input;

/**
 * ListPage Component
 * Pre-configured page for list/table views
 *
 * @param {string} title - Page title
 * @param {string} subtitle - Page subtitle
 * @param {Array} breadcrumbs - Breadcrumb items
 * @param {ReactNode} children - Table/List content
 * @param {boolean} showSearch - Show search bar
 * @param {string} searchPlaceholder - Search placeholder text
 * @param {function} onSearch - Search handler
 * @param {string} searchValue - Controlled search value
 * @param {ReactNode} filters - Custom filter components
 * @param {boolean} showCreate - Show create button
 * @param {string} createText - Create button text
 * @param {function} onCreate - Create button handler
 * @param {boolean} showExport - Show export button
 * @param {string} exportText - Export button text
 * @param {function} onExport - Export button handler
 * @param {ReactNode} customActions - Custom action buttons
 * @param {boolean} wrapInSection - Wrap content in Section component
 * @param {string} sectionTitle - Section title (if wrapped)
 * @param {object} sectionExtra - Section extra content (if wrapped)
 */
function ListPage({
  title,
  subtitle,
  breadcrumbs,
  children,
  // Search
  showSearch = true,
  searchPlaceholder = 'Search...',
  onSearch,
  searchValue,
  // Filters
  filters,
  // Actions
  showCreate = false,
  createText = 'Create',
  onCreate,
  showExport = false,
  exportText = 'Export',
  onExport,
  customActions,
  // Section wrapper
  wrapInSection = true,
  sectionTitle,
  sectionExtra,
}) {
  // Build header actions
  const headerActions = (
    <Space size="middle">
      {showExport && (
        <Button
          icon={<DownloadOutlined />}
          onClick={onExport}
        >
          {exportText}
        </Button>
      )}
      {showCreate && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onCreate}
        >
          {createText}
        </Button>
      )}
      {customActions}
    </Space>
  );

  // Build filter bar
  const filterBar = (showSearch || filters) && (
    <div
      style={{
        display: 'flex',
        gap: spacing.md,
        marginBottom: spacing.lg,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      {showSearch && (
        <Search
          placeholder={searchPlaceholder}
          onSearch={onSearch}
          onChange={(e) => onSearch?.(e.target.value)}
          value={searchValue}
          style={{ width: 300 }}
          allowClear
        />
      )}
      {filters}
    </div>
  );

  // Content with or without section wrapper
  const content = wrapInSection ? (
    <Section title={sectionTitle} extra={sectionExtra}>
      {filterBar}
      {children}
    </Section>
  ) : (
    <>
      {filterBar}
      {children}
    </>
  );

  return (
    <TrustGridPage
      title={title}
      subtitle={subtitle}
      breadcrumbs={breadcrumbs}
      actions={headerActions}
    >
      {content}
    </TrustGridPage>
  );
}

export default ListPage;
